import type { EPGEntryDto } from "$src/core/models/dto";

type ProgramTimeRange = { start: number; end: number };

const parseDateMs = (value?: string): number | undefined => {
  if (!value) {
    return undefined;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
};

const computeProgramTimeRange = (
  entry: EPGEntryDto,
): ProgramTimeRange | undefined => {
  const { item } = entry;
  const start = parseDateMs(item.startDate);
  if (start === undefined) {
    return undefined;
  }

  let end = parseDateMs(item.endDate);
  if (
    end === undefined &&
    item.duration != null &&
    Number.isFinite(item.duration) &&
    item.duration > 0
  ) {
    end = start + item.duration * 1000;
  }
  if (end === undefined || end <= start) {
    return undefined;
  }

  return { start, end };
};

type CachedRange = {
  startDate: string | undefined;
  endDate: string | undefined;
  duration: number | undefined;
  range: ProgramTimeRange | undefined;
};

// The same ISO strings are re-parsed on every day filter, row layout, live-state
// check and focus-anchor lookup. Cache the result by entry identity, revalidated
// against the source fields so an entry mutated in place (not just replaced by a
// refetch) still recomputes.
const rangeCache = new WeakMap<EPGEntryDto, CachedRange>();

const programTimeRangeMs = (
  entry: EPGEntryDto,
): ProgramTimeRange | undefined => {
  const { item } = entry;
  const cached = rangeCache.get(entry);
  if (
    cached !== undefined &&
    cached.startDate === item.startDate &&
    cached.endDate === item.endDate &&
    cached.duration === item.duration
  ) {
    return cached.range;
  }

  const range = computeProgramTimeRange(entry);
  rangeCache.set(entry, {
    startDate: item.startDate,
    endDate: item.endDate,
    duration: item.duration,
    range,
  });
  return range;
};

export default programTimeRangeMs;
